import type { PointMarkup } from '@technology/web3d-plugin-markup';
import type { Markup as ConnectMarkup } from '@data-access/connect/tc-tcps';
import {
  applyPointLabel,
  isAngleMeasurement,
  isLineMeasurement,
  isMeasurement,
  isPointMeasurement,
  MEASUREMENT_LABEL_PREFIX,
  type AngleMeasurementType,
  type LineMeasurementType,
  type Measurement,
  type MeasurementsCapabilityProvider,
  type PointMeasurementType,
} from '@trimble/web3d-providers';
import { getLogger } from '@trimble/logging';
import type { ConnectMeasurementData } from './multipane-view.types';

// Alignment is excluded: it isn't Connect-native and keeps its label in its own
// full reserved record, so only point/line/angle need a sparse {id, label} patch.
type LabeledMeasurement =
  | PointMeasurementType
  | LineMeasurementType
  | AngleMeasurementType;

type LabeledMeasurementType = LabeledMeasurement['type'];

// Mirrors convertMeasurementToConnectMarkup for the reorder guard below.
const MEASUREMENT_TO_CONNECT_TYPE: Record<LabeledMeasurementType, string> = {
  point: 'measure',
  line: 'measure_with_pick',
  angle: 'angle',
};

const LABEL_NUMBER_PATTERN: Record<LabeledMeasurementType, RegExp> = {
  point: /^Point (\d+)$/,
  line: /^Line (\d+)$/,
  angle: /^Angle (\d+)$/,
};

function isLabeledMeasurement(
  measurement: Measurement
): measurement is LabeledMeasurement {
  return (
    isPointMeasurement(measurement) ||
    isLineMeasurement(measurement) ||
    isAngleMeasurement(measurement)
  );
}

/**
 * Connect preserves submit order; drop all labels on count/type mismatch rather
 * than risk assigning a name to the wrong markup.
 */
export function collectConnectMeasurementData(
  measurements: readonly Measurement[],
  createdMarkups: readonly ConnectMarkup[]
): ConnectMeasurementData[] {
  const labeled = measurements.filter(isLabeledMeasurement);
  const measureMarkups = createdMarkups.filter(isMeasurement);

  if (labeled.length !== measureMarkups.length) {
    getLogger().warn(
      '[measurement-labels] Measurement/markup count mismatch; skipping label persistence',
      { measurements: labeled.length, markups: measureMarkups.length }
    );
    return [];
  }

  const result: ConnectMeasurementData[] = [];
  for (let i = 0; i < labeled.length; i++) {
    const measurement = labeled[i];
    const markup = measureMarkups[i];

    if (markup.type !== MEASUREMENT_TO_CONNECT_TYPE[measurement.type]) {
      getLogger().warn(
        '[measurement-labels] Measurement/markup type mismatch; skipping label persistence',
        {
          index: i,
          expected: MEASUREMENT_TO_CONNECT_TYPE[measurement.type],
          actual: markup.type,
        }
      );
      return [];
    }

    const label = measurement.label?.trim();
    if (markup.markupId && label) {
      result.push({ id: markup.markupId, label });
    }
  }

  return result;
}

/**
 * Restore saved labels and auto-name legacy views. Point markups also need their
 * formatter refreshed for the 3D label.
 */
export function restoreMeasurementLabels(
  provider: MeasurementsCapabilityProvider,
  labels: readonly ConnectMeasurementData[]
): void {
  const labelById = new Map(labels.map((entry) => [entry.id, entry.label]));

  for (const measurement of provider.items()) {
    if (!isLabeledMeasurement(measurement)) continue;
    const label = labelById.get(measurement.id);
    if (label !== undefined) {
      applyLabel(provider, measurement, label);
    }
  }

  const missing = provider
    .items()
    .filter(isLabeledMeasurement)
    .filter((measurement) => !measurement.label);

  const usedNumbersByType = new Map<LabeledMeasurementType, Set<number>>();
  for (const measurement of provider.items()) {
    if (!isLabeledMeasurement(measurement)) continue;
    if (!measurement.label) continue;
    const used = getUsedNumbers(usedNumbersByType, measurement.type);
    const match = LABEL_NUMBER_PATTERN[measurement.type].exec(
      measurement.label
    );
    if (match) used.add(Number(match[1]));
  }

  for (const measurement of missing) {
    const used = getUsedNumbers(usedNumbersByType, measurement.type);
    let next = 1;
    while (used.has(next)) next++;
    used.add(next);
    applyLabel(
      provider,
      measurement,
      `${MEASUREMENT_LABEL_PREFIX[measurement.type]} ${next}`
    );
  }
}

function getUsedNumbers(
  usedNumbersByType: Map<LabeledMeasurementType, Set<number>>,
  type: LabeledMeasurementType
): Set<number> {
  let used = usedNumbersByType.get(type);
  if (!used) {
    used = new Set<number>();
    usedNumbersByType.set(type, used);
  }
  return used;
}

function applyLabel(
  provider: MeasurementsCapabilityProvider,
  measurement: LabeledMeasurement,
  label: string
): void {
  if (isPointMeasurement(measurement)) {
    refreshPointMarkupLabel(provider, measurement, label);
  }
  provider.updateMeasurement({ ...measurement, label });
}

function refreshPointMarkupLabel(
  provider: MeasurementsCapabilityProvider,
  measurement: PointMeasurementType,
  label: string
): void {
  for (const viewerWrapper of provider.viewerWrappers()) {
    for (const markup of provider.getViewerMarkups(
      viewerWrapper.viewer,
      measurement
    )) {
      applyPointLabel(
        markup as PointMarkup,
        viewerWrapper.viewer,
        label,
        measurement.showXYCoordinates ?? true,
        measurement.showZCoordinate ?? true
      );
    }
  }
}
